import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { ApiError } from "../../api/client";
import { useMe } from "../../auth/useMe";
import { TagInput, COMMON_SPECIALISATIONS } from "../../shell/TagInput";
import { RbacApi, type Permission, type Role } from "./api";

// Server requires ^[a-z][a-z0-9_]+$ — turn "Snake Case" into "snake_case"
// so admins can type human-friendly text in the form and we save the ID.
function slugifyRoleId(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);
}

// One-line hint per resource group. The resource string (e.g. "case") is
// what the API returns; we annotate it here so the role editor reads as
// English instead of jargon.
const RESOURCE_HINTS: Record<string, string> = {
  assignment: "Who holds which role at which scope (RBAC admin)",
  audit: "Audit log — who did what, when",
  branch: "Branches in the hierarchy (admin)",
  case: "Investigation cases on the Cases page",
  constituency: "Constituencies in the hierarchy (admin)",
  intel: "Intel feed — alerts, observations, notes",
  performance: "Performance reports submitted by branches",
  pii: "Reveal masked personal info (names, ID numbers, phone numbers)",
  poi: "People of Interest watchlist",
  popularity: "Popularity reports submitted by branches",
  position: "Org-chart titles (admin)",
  report: "Reports dashboards & CSV exports — NOT operational data",
  role: "Role definitions and their permissions (admin)",
  screen: "UI screens & navigation menu (admin)",
  task: "Task assignments people can give each other",
  user: "User accounts (admin)",
  ward: "Wards in the hierarchy (admin)",
};

// Friendly labels for the action verbs so a fresh admin can guess what
// each box does without clicking around.
const ACTION_LABELS: Record<string, string> = {
  create: "Create new records",
  read: "View records",
  update: "Edit records",
  delete: "Delete records",
  publish: "Publish new entries",
  export: "Download as CSV / file",
  generate: "Run reports",
  read_classified: "View Restricted/Secret records",
};

export function Roles() {
  const qc = useQueryClient();
  const { isSuperadmin } = useMe();
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const roles = useQuery({ queryKey: ["roles"], queryFn: RbacApi.listRoles });
  const perms = useQuery({ queryKey: ["permissions"], queryFn: RbacApi.listPermissions });

  const remove = useMutation({
    mutationFn: RbacApi.deleteRole,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["roles"] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : (e as Error).message),
  });

  return (
    <div className="hierarchy">
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <div>
          <h1>Roles</h1>
          <p className="muted">
            Bundles of permissions. Users hold one or more roles via assignments.
          </p>
        </div>
        {isSuperadmin && !creating && (
          <button onClick={() => setCreating(true)}>+ Create role</button>
        )}
      </header>
      {error && <div role="alert" className="error">{error}</div>}

      {isSuperadmin && creating && (
        <NewRoleForm
          permissions={perms.data ?? []}
          onCreated={() => {
            qc.invalidateQueries({ queryKey: ["roles"] });
            setCreating(false);
          }}
          onCancel={() => setCreating(false)}
        />
      )}

      <ul className="tree">
        {roles.data?.items.map((r) => (
          <li key={r.id}>
            <div className="row">
              <span>
                <strong>{r.name}</strong>{" "}
                <span className="muted">({r.id}{r.isSystem ? ", system" : ""})</span>
                <div className="muted" style={{ fontSize: 12 }}>
                  {r.description || "—"} · {r.permissions.length} permission(s)
                </div>
              </span>
              <span className="row-actions">
                {(isSuperadmin || editing === r.id) && (
                  <button className="ghost" onClick={() => setEditing(editing === r.id ? null : r.id)}>
                    {editing === r.id ? "Close" : (isSuperadmin && !r.isSystem ? "Edit permissions" : "View permissions")}
                  </button>
                )}
                {/* Delete removed — roles are kept for the audit history of users
                    who held them. Edit the role's permissions instead to retire it. */}
              </span>
            </div>
            {editing === r.id && (
              <RoleEditor
                role={r}
                permissions={perms.data ?? []}
                canEdit={isSuperadmin && !r.isSystem}
                onSaved={() => setEditing(null)}
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function NewRoleForm({
  permissions,
  onCreated,
  onCancel,
}: {
  permissions: Permission[];
  onCreated: () => void;
  onCancel: () => void;
}) {
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [specialisations, setSpecialisations] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: RbacApi.createRole,
    onSuccess: () => {
      setId(""); setName(""); setDesc(""); setSelected(new Set());
      onCreated();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : "Failed"),
  });

  // Live preview of the slugified ID. If the admin doesn't fill the ID box,
  // we derive one from the display name on submit.
  const idPreview = slugifyRoleId(id) || (name ? slugifyRoleId(name) : "");

  return (
    <form
      className="create-role"
      onSubmit={(e) => {
        e.preventDefault();
        const finalId = slugifyRoleId(id) || slugifyRoleId(name);
        if (!finalId) { setErr("Give the role a name or ID."); return; }
        if (selected.size === 0 &&
            !window.confirm("This role has no permissions ticked — anyone given it will be able to do nothing. Create it anyway?")) {
          return;
        }
        create.mutate({ id: finalId, name, description: desc, permissions: [...selected], specialisations });
      }}
    >
      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
        Pick a clear display name (e.g. "Intelligence Officer") and tick the boxes for what people in this role
        should be able to do. The ID is the machine-readable handle — leave it blank to auto-generate one.
      </div>
      <div className="row-3">
        <input value={id} onChange={(e) => setId(e.target.value)} placeholder="role_id — leave blank to auto-generate" />
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Display name" required />
        <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description (what this role is for)" />
      </div>
      <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
        {idPreview
          ? <>The ID will be saved as: <code>{idPreview}</code></>
          : "Lowercase letters, numbers, and underscores. Spaces become underscores."}
      </div>
      <div style={{ marginTop: 14 }}>
        <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Specialisations (optional)</label>
        <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>
          Skill tags inherited by every user holding this role. The task wizard uses them to suggest who should action a task.
        </div>
        <TagInput
          value={specialisations}
          onChange={setSpecialisations}
          placeholder="e.g. surveillance, cyber, logistics — Enter to add"
          suggestions={COMMON_SPECIALISATIONS}
        />
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 12, marginBottom: 4 }}>
        <strong>Permissions.</strong> Each group below is a <em>resource</em>, with a hint of what it controls.
        Each checkbox is an <em>action</em> on that resource. Only tick what this role genuinely needs —
        if you tick everything, the role becomes effectively a superadmin.
      </div>
      <PermissionMatrix
        all={permissions}
        selected={selected}
        onToggle={(k) => {
          const next = new Set(selected);
          next.has(k) ? next.delete(k) : next.add(k);
          setSelected(next);
        }}
      />
      <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
        <button type="submit" disabled={create.isPending}>
          {create.isPending ? "Creating…" : "Create role"}
        </button>
        <button type="button" className="ghost" onClick={onCancel} disabled={create.isPending}>
          Cancel
        </button>
        {err && <span className="error" style={{ marginLeft: 12 }}>{err}</span>}
      </div>
    </form>
  );
}

function RoleEditor({
  role,
  permissions,
  canEdit,
  onSaved,
}: {
  role: Role;
  permissions: Permission[];
  canEdit: boolean;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(role.name);
  const [desc, setDesc] = useState(role.description ?? "");
  const [selected, setSelected] = useState<Set<string>>(new Set(role.permissions));

  const save = useMutation({
    mutationFn: () =>
      RbacApi.updateRole(role.id, { name, description: desc, permissions: [...selected] }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["roles"] });
      onSaved();
    },
  });

  return (
    <div className="children">
      {!canEdit ? (
        <p className="muted">
          {role.isSystem
            ? "System role — view only. The boxes below show what this role currently grants."
            : "View only. Ask a superadmin to change role permissions."}
        </p>
      ) : (
        <>
          <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
            Untick the boxes you added by mistake, or tick new ones. Click <strong>Save</strong> when you're done.
            Anyone holding this role sees the change as soon as their session refreshes.
          </div>
          <div className="row-3">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Display name" />
            <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description" />
            <button
              onClick={() => {
                if (window.confirm(`Save changes to role "${role.name}"? Anyone holding it sees their access change as soon as their session refreshes.`))
                  save.mutate();
              }}
              disabled={save.isPending}
            >
              {save.isPending ? "Saving…" : "Save"}
            </button>
          </div>
        </>
      )}
      <PermissionMatrix
        all={permissions}
        selected={selected}
        readOnly={!canEdit}
        onToggle={(k) => {
          const next = new Set(selected);
          next.has(k) ? next.delete(k) : next.add(k);
          setSelected(next);
        }}
      />
    </div>
  );
}

function PermissionMatrix({
  all,
  selected,
  onToggle,
  readOnly = false,
}: {
  all: Permission[];
  selected: Set<string>;
  onToggle: (key: string) => void;
  readOnly?: boolean;
}) {
  const grouped = useMemo(() => {
    const m: Record<string, Permission[]> = {};
    for (const p of all) (m[p.resource] ??= []).push(p);
    for (const list of Object.values(m)) list.sort((a, b) => a.action.localeCompare(b.action));
    return m;
  }, [all]);

  return (
    <div className="perm-matrix">
      {Object.entries(grouped)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([resource, list]) => (
          <fieldset key={resource}>
            <legend style={{ textTransform: "capitalize" }}>{resource}</legend>
            <div className="muted" style={{ fontSize: 11, marginBottom: 6 }}>
              {RESOURCE_HINTS[resource] || "—"}
            </div>
            {list.map((p) => (
              <label
                key={p.id}
                title={p.description}
                style={{ display: "block", marginBottom: 4, lineHeight: 1.3 }}
              >
                <input
                  type="checkbox"
                  checked={selected.has(p.id)}
                  disabled={readOnly}
                  onChange={() => onToggle(p.id)}
                  style={{ marginRight: 6 }}
                />
                <span style={{ fontWeight: 500 }}>{ACTION_LABELS[p.action] || p.action}</span>
                {p.description && (
                  <div className="muted" style={{ fontSize: 11, marginLeft: 22 }}>
                    {p.description}
                  </div>
                )}
              </label>
            ))}
          </fieldset>
        ))}
    </div>
  );
}
